import { useCallback, useEffect } from 'react';

import type { ProductReviewResponse } from '~/interfaces/product';

import { useNonRevalidatingFetcher } from './useNonRevalidatingFetcher';

interface Props {
  productSlug: string;
  reviewId: string | null;
  enabled?: boolean;
}

interface ReturnProps {
  isLoading: boolean;
  data: ProductReviewResponse | null;
  enabled?: boolean;
  refetch: () => void;
}

const useStaticProductReview = ({ productSlug, reviewId, enabled = true }: Props): ReturnProps => {
  const fetcher = useNonRevalidatingFetcher<() => Promise<ProductReviewResponse>>();
  const fetchedData = fetcher?.data;

  const fetchData = useCallback(() => {
    fetcher.load(`/product_reviews?slug=${productSlug}&review_id=${reviewId}`);
  }, [fetcher, reviewId, productSlug]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: If we included fetcher, it would refetch constantly
  useEffect(() => {
    if (enabled) {
      fetchData();
    }
  }, [enabled]);

  const isLoading = fetcher.state === 'loading' || (!fetcher?.data && enabled);

  return {
    refetch: fetchData,
    isLoading: isLoading && enabled,
    data: fetchedData || null,
  };
};

export default useStaticProductReview;
